<template>
  <div
    class="w-full rounded-lg bg-background px-6 py-3 shadow"
  >
    <div
      class="flex flex-row items-center justify-start space-x-2"
    >
      <div class="flex flex-col justify-center">
        <p
          class="line-clamp-1 w-9 text-left text-sm font-semibold"
        >
          {{ formatToTime(report.createdAt) }}
        </p>
      </div>

      <div class="flex flex-1 justify-start">
        <span
          class="mr-2 line-clamp-1 text-left text-xs text-background-fg/70"
        >
          {{ description }}
        </span>
      </div>

      <p
        class="min-w-12 text-right font-semibold text-background-fg"
      >
        {{ formattedTotal }}
      </p>
    </div>
    <div
      class="-ml-1.5 mt-0.5 flex flex-row items-center justify-between"
    >
      <span
        class="inline-flex shrink-0 items-center rounded-full px-2 py-px text-xs font-semibold text-white"
        :style="{ backgroundColor: deviceColor }"
      >
        {{ report.deviceSerialNumber }}
      </span>

      <span
        v-if="showZReportPill"
        class="inline-flex shrink-0 items-center rounded-full border border-background-fg/20 bg-background-fg/6 px-2 py-px text-[0.625rem] font-semibold uppercase tracking-wide text-background-fg/75"
      >
        Z Report
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ListZReportsResponseItem } from "@/services/zReports/listZReports"
import type { TransactionsFeedZReport } from "@/services/orders/listTransactionsFeed"

const props = withDefaults(
  defineProps<{
    report:
      | ListZReportsResponseItem
      | TransactionsFeedZReport
    showZReportPill?: boolean
  }>(),
  { showZReportPill: true }
)

const { formatToMerchantCurrencyString } = useCurrency()
const { data: devices } = useDevices()

const colorOptions = useColorOptions().options

const deviceColor = computed(() => {
  const idx =
    devices.value?.findIndex(
      ({ id }) => id === props.report.deviceId
    ) ?? -1
  const colorVar =
    idx >= 0
      ? colorOptions[idx % colorOptions.length].value
      : colorOptions[0].value
  return `rgb(var(${colorVar}))`
})

const formattedTotal = computed(() => {
  if (props.report.totalAmount === null) {
    return "—"
  }
  return formatToMerchantCurrencyString(
    props.report.totalAmount
  )
})

const description = computed(() => {
  const { fromReceiptNo, toReceiptNo, totalCount } =
    props.report

  if (fromReceiptNo != null && toReceiptNo != null) {
    return `#${fromReceiptNo} - #${toReceiptNo}`
  }

  if (totalCount == null) {
    return "Count unknown"
  } else if (totalCount === 0) {
    return "No transactions"
  } else if (totalCount === 1) {
    return "1 transaction"
  } else {
    return `${totalCount} transactions`
  }
})
</script>
